import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthProvider";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { MASCARA_CPF, MASCARA_TELEFONE } from "@/utils/Constants";
import { InputFormik } from "@/components/ui/input-formik";
import { InputMask } from "@/components/ui/input-mask";
import AcessoService, { BuscarDadosClientePrimeiroAcesso } from "@/services/AcessoService";
import { normalizarCPF, validarCPF } from "@/utils/Cpf";

export interface ConfirmarDadosCadastro {
    Login: string,
    Telefone: string,
    senha: string,
    confirmarSenha: string,
}

export function PrimeiroAcessoPage() {
    const { toast } = useToast();
    const navigate = useNavigate();
    const [dadosCliente, setDadosCliente] = useState<BuscarDadosClientePrimeiroAcesso>()
    let [searchParams] = useSearchParams();
    const { cadastrarSenhaPrimeiroAcesso } = useAuth()

    useEffect(() => {
        async function initialLoad() {
            const login = searchParams.get('login');

            if (!login) {
                navigate('/')
                return;
            }

            const { Data: result } = await AcessoService.buscarDadosClientePrimeiroAcesso(login);
            setDadosCliente(result);
        }

        initialLoad()
    }, [])

    const formikFormConfirmarCadastro = useFormik<ConfirmarDadosCadastro>({
        initialValues: {
            Login: '',
            Telefone: '',
            senha: '',
            confirmarSenha: ''
        },
        validationSchema: Yup.object({
            Login: Yup.string()
                .required('Obrigatório')
                .test('Validar CPF', 'CPF Inválido', cpfToValidate => {
                    return validarCPF(cpfToValidate || '');
                }),
            Telefone: Yup.string()
                .required('Obrigatório')
                .test('Validar Telefone', 'Telefone Inválido', telefoneToValidate => {
                    if (telefoneToValidate
                        .replace(/_/g, '')
                        .replace(/\(/g, '')
                        .replace(/\)/g, '')
                        .replace(/-/g, '')
                        .replace(/\./g, '')
                        .replace(/\s/g, '')
                        .length !== 11) {
                        return false;
                    }

                    return true;
                }),
            senha: Yup.string().required('Obrigatório'),
            confirmarSenha: Yup.string()
                .min(5, 'Deve possuir ao menos 5 caracteres')
                .required('Obrigatório')
                .test('Validar Confirmar Senha', 'Digite as duas senhas iguais', passwordToValidate => {
                    if (passwordToValidate !== formikFormConfirmarCadastro.values.senha) {
                        return false;
                    }

                    return true;
                })
        }),
        onSubmit: async (values) => {
            try {
                const dados = {
                    Login: normalizarCPF(values.Login),
                    Telefone: values.Telefone
                    .replace(/_/g, '')
                    .replace(/\(/g, '')
                    .replace(/\)/g, '')
                    .replace(/-/g, '')
                    .replace(/\./g, '')
                    .replace(/\s/g, ''),
                    senha: values.senha
                } as ConfirmarDadosCadastro;

                await cadastrarSenhaPrimeiroAcesso(dados);
                toast({
                    description: 'Cadastro Realizado com sucesso!',
                    duration: 3000,
                    variant: 'success'
                })

                navigate('/')
            } catch (error: any) {
                toast({
                    variant: 'destructive',
                    description: error,
                    duration: 4000
                });
            }
        }
    });


    return (
        <Card className="mx-auto max-w-sm my-4">
            <CardHeader>
                <CardTitle className="text-2xl text-primary-foreground">Primeiro Acesso</CardTitle>

                <CardDescription>
                    Confirme seus dados para finalizar o cadastro da senha

                    <p><span className="font-bold">Telefone: </span>{dadosCliente?.TelefoneMasked}</p>
                    <p><span className="font-bold">CPF: </span>{dadosCliente?.CPFMasked}</p>
                </CardDescription>

            </CardHeader>
            <CardContent>


                <form onSubmit={formikFormConfirmarCadastro.handleSubmit} className="grid gap-4 text-primary-foreground">

                    <Label htmlFor="CPF">CPF</Label>
                    <InputMask
                        placeholder="CPF"
                        type="text"
                        id="Login"
                        required
                        formik={formikFormConfirmarCadastro}
                        name='Login'
                        mask={MASCARA_CPF}
                    />
                    {
                        (formikFormConfirmarCadastro.touched['Login'] && formikFormConfirmarCadastro.errors['Login']) &&
                        (
                            <span className="text-sm text-destructive">
                                {formikFormConfirmarCadastro.errors['Login']}
                            </span>
                        )
                    }

                    <Label htmlFor="Telefone">Telefone</Label>
                    <InputMask
                        mask={MASCARA_TELEFONE}
                        placeholder="Telefone"
                        type="text"
                        id="Telefone"
                        required
                        formik={formikFormConfirmarCadastro}
                        name='Telefone'
                    />


                    <Label htmlFor="senha">Senha</Label>
                    <InputFormik
                        placeholder="Senha"
                        type="password"
                        id="senha"
                        required
                        formik={formikFormConfirmarCadastro}
                        name='senha'
                    />

                    <Label htmlFor="confirmarSenha">Confirme sua Senha</Label>
                    <InputFormik
                        placeholder="Digite sua senha novamente"
                        type="password"
                        id="confirmarSenha"
                        required
                        formik={formikFormConfirmarCadastro}
                        name='confirmarSenha'
                    />
                    <Button
                        disabled={formikFormConfirmarCadastro.errors.Login !== undefined || formikFormConfirmarCadastro.errors.senha !== undefined || formikFormConfirmarCadastro.isSubmitting}
                        tabIndex={3}
                        type="submit"
                        className="w-full">
                        {formikFormConfirmarCadastro.isSubmitting ? 'Finalizando...' : 'Acessar'}
                    </Button>
                </form>
            </CardContent>
        </Card>
    )
}